import { useState, useEffect, useCallback } from 'react';
import { wikiTocApi, wikiArticleApi } from './api';

/**
 * A documentation section. `description` is the section NAME; `summary` is the
 * blurb shown on the landing-page card (nullable — added by
 * infrastructure/sql/migration-add-toc-summary.sql).
 */
export interface WikiTOC {
    id: string;
    parentId: string | null;
    description: string;
    summary?: string | null;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    children?: WikiTOC[];
}

/** The article fields the navigation and landing page need. */
export interface WikiArticleSummary {
    id: string;
    tocId: string;
    title: string;
    tags: string | null;
    updatedAt: string;
    updatedByName?: string | null;
}

/**
 * The API has returned both PascalCase and camelCase for these over time, so
 * normalize defensively — and recursively, since children arrive nested.
 */
export const normalizeToc = (raw: any[]): WikiTOC[] =>
    (Array.isArray(raw) ? raw : []).map((t: any) => ({
        ...t,
        id: t.id ?? t.Id,
        parentId: t.parentId ?? t.ParentId ?? null,
        description: t.description ?? t.Description ?? '',
        summary: t.summary ?? t.Summary ?? null,
        sortOrder: t.sortOrder ?? t.SortOrder ?? 0,
        children: normalizeToc(t.children ?? t.Children ?? []),
    }));

export const normalizeArticles = (raw: any[]): WikiArticleSummary[] =>
    (Array.isArray(raw) ? raw : []).map((a: any) => ({
        ...a,
        id: a.id ?? a.Id,
        tocId: a.tocId ?? a.TOCId,
        title: a.title ?? a.Title ?? '',
        tags: a.tags ?? a.Tags ?? null,
        updatedAt: a.updatedAt ?? a.UpdatedAt,
        updatedByName: a.updatedByName ?? a.UpdatedByName ?? null,
    }));

/** Depth-first lookup of a single section anywhere in the tree. */
export const findSection = (items: WikiTOC[], id: string | null | undefined): WikiTOC | null => {
    if (!id) return null;
    for (const item of items) {
        if (item.id === id) return item;
        const found = findSection(item.children || [], id);
        if (found) return found;
    }
    return null;
};

/** Root-first chain of sections leading to `id`, inclusive. Empty if not found. */
export const findSectionPath = (items: WikiTOC[], id: string | null | undefined): WikiTOC[] => {
    if (!id) return [];
    for (const item of items) {
        if (item.id === id) return [item];
        const below = findSectionPath(item.children || [], id);
        if (below.length > 0) return [item, ...below];
    }
    return [];
};

/** The top-level section a given section sits under (itself, if it is one). */
export const findRootSection = (items: WikiTOC[], id: string | null | undefined): WikiTOC | null =>
    findSectionPath(items, id)[0] ?? null;

/** A section's own id plus every descendant id. */
export const collectSectionIds = (section: WikiTOC): string[] => [
    section.id,
    ...(section.children || []).flatMap(collectSectionIds),
];

/** Articles filed directly against one section. */
export const articlesIn = (articles: WikiArticleSummary[], sectionId: string): WikiArticleSummary[] =>
    articles
        .filter(a => a.tocId === sectionId)
        .sort((a, b) => a.title.localeCompare(b.title));

/** Articles anywhere beneath a section, including the section itself. */
export const articlesUnder = (articles: WikiArticleSummary[], section: WikiTOC): WikiArticleSummary[] => {
    const ids = collectSectionIds(section);
    return articles.filter(a => ids.includes(a.tocId));
};

interface UseWikiDocsOptions {
    /** Landing and navigation show published articles only; the manage screen wants all. */
    publishedOnly?: boolean;
}

/**
 * Loads the section tree and article list together — every documentation
 * screen needs both to build its navigation.
 */
export const useWikiDocs = ({ publishedOnly = true }: UseWikiDocsOptions = {}) => {
    const [sections, setSections] = useState<WikiTOC[]>([]);
    const [articles, setArticles] = useState<WikiArticleSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);

        // fetchApi reports an HTTP error in its result, but still throws when
        // the request never completes — the API host being down, CORS, a
        // response that is not JSON. Without the catch that rejection escapes
        // and the screen spins forever instead of saying what went wrong.
        try {
            const [tocResult, articleResult] = await Promise.all([
                wikiTocApi.getAll(),
                wikiArticleApi.getAll(publishedOnly ? { isPublished: true } : undefined),
            ]);

            if (tocResult.error) {
                setError(tocResult.error);
                setSections([]);
            } else {
                setSections(normalizeToc(tocResult.data || []));
            }

            if (articleResult.error) {
                console.error('Failed to fetch articles:', articleResult.error);
                setArticles([]);
            } else {
                setArticles(normalizeArticles(articleResult.data || []));
            }
        } catch (e) {
            console.error('Failed to load documentation:', e);
            setError(
                `Could not reach the documentation API (${
                    e instanceof Error ? e.message : String(e)
                }). Check that the Functions host is running.`
            );
            setSections([]);
            setArticles([]);
        } finally {
            setLoading(false);
        }
    }, [publishedOnly]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { sections, articles, loading, error, refresh };
};
